import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Building2,
  Calendar,
  CalendarClock,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Clock,
  FileText,
  Link2,
  MessageCircle,
  User,
  Users,
  X,
} from 'lucide-react';
import {
  AqilaButton,
  AqilaFooterBar,
  AqilaTooltip,
} from '@agro1desenvolvimento/agro1-design-system/aqila';
import RelatorioVisitaService from '../../../services/relatorio-visita';
import useAsyncFetch from '../../../hooks/use-async';
import { orDash } from '../../../utils/schema-parse';
import { formatFullDate } from '../../../utils/format-date';
import type {
  RelatorioVisitaGrupo,
  RelatorioVisitaResumoOcorrencias,
  RelatorioVisitaTalhao,
} from '../schemas/payload.schema';
import GrupoSection from './components/grupo-section';
import RecomendacoesSection from './components/recomendacoes-section';
import ChuvaSection from './components/chuva-section';
import logo from '../../../assets/aqila.png';
import agro1Logo from '../../../assets/agro1-logo.svg';

type Tab =
  | {
      kind: 'grupo';
      key: string;
      label: string;
      gleba: RelatorioVisitaTalhao['gleba'];
      grupo: RelatorioVisitaGrupo;
      resumoOcorrencias?: RelatorioVisitaResumoOcorrencias;
    }
  | { kind: 'recomendacoes'; key: string; label: string }
  | { kind: 'chuva'; key: string; label: string };

const AVATAR_COLORS = ['#16a34a', '#2563eb', '#7c3aed', '#d97706'];

const LINK_COPIADO_FEEDBACK_MS = 2000;

const SPY_IDLE_MS = 200;

const copiarParaClipboard = async (texto: string) => {
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch {
    const campo = document.createElement('textarea');
    campo.value = texto;
    campo.style.position = 'fixed';
    campo.style.opacity = '0';
    document.body.appendChild(campo);
    campo.select();
    const copiado = document.execCommand('copy');
    document.body.removeChild(campo);
    return copiado;
  }
};

const getInitials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0] ?? '')
    .join('')
    .toUpperCase();

const Pill = ({
  label,
  variant,
  active = false,
  onClick,
}: {
  label: string;
  variant: 'talhao' | 'especial';
  active?: boolean;
  onClick: () => void;
}) => (
  <button
    className={`rv-nav-pill rv-nav-pill--${
      variant === 'talhao' ? 'talhao' : 'especial'
    }${active ? ' rv-nav-pill--active' : ''}`}
    type="button"
    onClick={onClick}
  >
    {label}
  </button>
);

const SummaryItem = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) => (
  <div className="rv-view-summary-item">
    <span className="rv-view-summary-icon">{icon}</span>
    <span className="rv-view-summary-label">{label}:</span>
    <span className="rv-view-summary-value">{value}</span>
  </div>
);

const RelatorioVisitaViewPage = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const [situacaoOpen, setSituacaoOpen] = useState(true);
  const [headerCollapsed, setHeaderCollapsed] = useState(false);
  const [activeTabKey, setActiveTabKey] = useState<string | null>(null);
  const [monitorModalOpen, setMonitorModalOpen] = useState(false);
  const [linkCopiado, setLinkCopiado] = useState(false);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const stickyHeaderRef = useRef<HTMLDivElement | null>(null);
  const pageRef = useRef<HTMLDivElement | null>(null);
  const spyLockedRef = useRef(false);
  const spyIdleTimerRef = useRef(0);

  const { data: relatorio } = useAsyncFetch(
    {
      withLoader: true,
      callback: async () => {
        if (!uuid) return undefined;
        return RelatorioVisitaService.findPublic(uuid);
      },
      errorMessage: 'Falha ao carregar o relatório de visita.',
    },
    [uuid],
  );

  const tabs = useMemo<Tab[]>(() => {
    if (!relatorio) return [];

    const grupoTabs: Tab[] = (relatorio.talhoes ?? []).flatMap(
      (talhao, talhaoIndex) =>
        (talhao.grupo ?? []).map((grupo, grupoIndex) => {
          const glebaNome = orDash(talhao.gleba?.descricao).toUpperCase();
          const cultura = grupo.cabecalho?.descricao;
          const hasCultura = Boolean(cultura) && cultura !== '-';

          return {
            kind: 'grupo' as const,
            key: `grupo-${talhaoIndex}-${grupoIndex}`,
            label: hasCultura ? `${glebaNome} · ${cultura}` : glebaNome,
            gleba: talhao.gleba,
            grupo,
            resumoOcorrencias:
              grupoIndex === 0 ? talhao.resumoOcorrencias : undefined,
          };
        }),
    );

    if ((relatorio.recomendacoes ?? []).length) {
      grupoTabs.push({
        kind: 'recomendacoes',
        key: 'recomendacoes',
        label: 'Recomendações Técnicas',
      });
    }

    if (relatorio.chuvaAcumulada) {
      grupoTabs.push({
        kind: 'chuva',
        key: 'chuva',
        label: 'Acumulado de Chuva',
      });
    }

    return grupoTabs;
  }, [relatorio]);

  const grupoKeys = useMemo(
    () => tabs.filter((tab) => tab.kind === 'grupo').map((tab) => tab.key),
    [tabs],
  );

  useEffect(() => {
    const node = pageRef.current;

    const COLLAPSE_AT = 96;
    const EXPAND_AT = 48;
    const SPY_OFFSET = 24;

    let frame = 0;

    const activeSectionKey = () => {
      const headerHeight =
        stickyHeaderRef.current?.getBoundingClientRect().height ?? 0;
      const line = headerHeight + SPY_OFFSET;

      const crossing = grupoKeys.filter((key) => {
        const rect = sectionRefs.current[key]?.getBoundingClientRect();
        return rect && rect.top <= line && rect.bottom > line;
      });
      if (crossing.length) return crossing[crossing.length - 1];

      const upcoming = grupoKeys.find((key) => {
        const rect = sectionRefs.current[key]?.getBoundingClientRect();
        return rect && rect.top > line && rect.top < window.innerHeight;
      });
      return upcoming ?? null;
    };

    const onScroll = () => {
      const scrollPosition = Math.max(node?.scrollTop ?? 0, window.scrollY);

      setHeaderCollapsed((previous) => {
        if (!previous && scrollPosition > COLLAPSE_AT) return true;
        if (previous && scrollPosition < EXPAND_AT) return false;
        return previous;
      });

      if (spyLockedRef.current) {
        if (frame) {
          window.cancelAnimationFrame(frame);
          frame = 0;
        }
        window.clearTimeout(spyIdleTimerRef.current);
        spyIdleTimerRef.current = window.setTimeout(() => {
          spyLockedRef.current = false;
          setActiveTabKey(activeSectionKey());
        }, SPY_IDLE_MS);
        return;
      }

      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        setActiveTabKey(activeSectionKey());
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    node?.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(spyIdleTimerRef.current);
      window.removeEventListener('scroll', onScroll);
      node?.removeEventListener('scroll', onScroll);
    };
  }, [relatorio, grupoKeys]);

  const totalPontos = useMemo(
    () =>
      (relatorio?.talhoes ?? []).reduce(
        (total, talhao) =>
          total +
          (talhao.grupo ?? []).reduce(
            (acc, grupo) => acc + (grupo.pontos ?? []).length,
            0,
          ),
        0,
      ),
    [relatorio],
  );

  const scrollToSection = (key: string) => {
    setActiveTabKey(key.startsWith('grupo-') ? key : null);

    const resetSpyIdle = (callback: () => void) => {
      if (spyIdleTimerRef.current !== null) {
        clearTimeout(spyIdleTimerRef.current);
      }
      spyIdleTimerRef.current = window.setTimeout(callback, SPY_IDLE_MS);
    };

    spyLockedRef.current = true;
    resetSpyIdle(() => {
      spyLockedRef.current = false;
    });

    const section = sectionRefs.current[key];

    if (!section) return;

    const headerHeight =
      stickyHeaderRef.current?.getBoundingClientRect().height ?? 0;

    section.style.scrollMarginTop = `${headerHeight + 12}px`;
    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const copiarLink = async () => {
    const copiado = await copiarParaClipboard(window.location.href);

    if (!copiado) return;

    setLinkCopiado(true);
    setTimeout(() => setLinkCopiado(false), LINK_COPIADO_FEEDBACK_MS);
  };

  if (!relatorio) return null;

  const { header } = relatorio;
  const monitores = header?.monitor ?? [];
  const monitorExtra = monitores.length > 1 ? monitores.length - 1 : 0;
  const situacaoGeral = header?.situacaoGeral;
  const hasSituacao = situacaoGeral && situacaoGeral !== '-';

  const grupoTabs = tabs.filter(
    (tab): tab is Extract<Tab, { kind: 'grupo' }> => tab.kind === 'grupo',
  );
  const especialTabs = tabs.filter(
    (tab): tab is Exclude<Tab, { kind: 'grupo' }> => tab.kind !== 'grupo',
  );

  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(
    `Relatório de Visita AQILA: ${window.location.href}`,
  )}`;

  const monitorTooltip = (
    <div className="rv-view-tooltip">
      <p className="rv-view-tooltip-title">Monitores:</p>
      <div className="rv-view-tooltip-list">
        {monitores.map((monitor) => (
          <div
            key={monitor.id ?? monitor.nome}
            className="rv-view-tooltip-item"
          >
            <span className="rv-view-tooltip-dot" />
            {orDash(monitor.nome)}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="rv-page" ref={pageRef}>
      <div className="rv-sticky-header" ref={stickyHeaderRef}>
        <div className="rv-bar">
          <Link className="rv-view-crumb-link" to="/relatorio-visita">
            <FileText className="rv-view-crumb-active" size={16} />
            <span className="rv-view-crumb-active">Relatórios</span>
          </Link>
          <ChevronRight className="rv-view-crumb-sep" size={16} />
          <span className="rv-view-crumb-current">
            {relatorio.id
              ? `Relatório nº ${relatorio.id}`
              : 'Relatório de Visita'}
          </span>
        </div>

        <div className="rv-header">
          <div
            className={`rv-view-header-top${
              headerCollapsed ? ' rv-view-header-top--collapsed' : ''
            }`}
          >
            <img
              alt="Logo"
              className="rv-view-logo"
              src={relatorio.header?.logomarca || logo}
            />
            <div className="rv-no-print rv-view-actions">
              <AqilaButton asChild variant="outline">
                <a href={whatsappHref} rel="noreferrer" target="_blank">
                  <MessageCircle className="rv-view-icon-green" />
                  <span className="rv-btn-label">Compartilhar WhatsApp</span>
                </a>
              </AqilaButton>
              <AqilaButton variant="outline" onClick={copiarLink}>
                {linkCopiado ? (
                  <Check className="rv-view-icon-green" />
                ) : (
                  <Link2 className="rv-view-icon-green" />
                )}
                <span className="rv-btn-label">
                  {linkCopiado ? 'Link copiado' : 'Copiar link'}
                </span>
              </AqilaButton>
            </div>
          </div>

          <div className="rv-view-card rv-view-summary-card">
            <h1 className="rv-view-title">Relatório de Visita Técnica</h1>
            <div className="rv-summary">
              <SummaryItem
                icon={<Building2 size={16} />}
                label="Propriedade"
                value={`${orDash(
                  header?.propriedade?.descricao,
                )} - Safra ${orDash(header?.safra?.descricao)}`}
              />
              <SummaryItem
                icon={<User size={16} />}
                label="Responsável"
                value={orDash(header?.responsavel?.nome)}
              />
              <AqilaTooltip content={monitorTooltip}>
                <button
                  className="rv-view-summary-item rv-view-summary-btn"
                  type="button"
                  onClick={() => setMonitorModalOpen(true)}
                >
                  <span className="rv-view-summary-icon">
                    <Users size={16} />
                  </span>
                  <span className="rv-view-summary-label">Monitor:</span>
                  <span className="rv-view-summary-value">
                    {orDash(monitores[0]?.nome)}
                  </span>
                  {monitorExtra ? (
                    <span className="rv-view-monitor-extra">
                      +{monitorExtra}
                    </span>
                  ) : null}
                </button>
              </AqilaTooltip>
              <SummaryItem
                icon={<Calendar size={16} />}
                label="Data"
                value={formatFullDate(header?.dataVisita)}
              />
              {header?.duracao && header.duracao !== '-' ? (
                <SummaryItem
                  icon={<Clock size={16} />}
                  label="Duração"
                  value={header.duracao}
                />
              ) : null}
            </div>
          </div>
        </div>

        {tabs.length > 0 ? (
          <div className="rv-no-print rv-nav">
            <span className="rv-nav-label">Navegar:</span>
            {tabs.map((tab) => (
              <Pill
                key={tab.key}
                active={tab.kind === 'grupo' && activeTabKey === tab.key}
                label={tab.label}
                variant={tab.kind === 'grupo' ? 'talhao' : 'especial'}
                onClick={() => scrollToSection(tab.key)}
              />
            ))}
          </div>
        ) : null}
      </div>

      <div className="rv-content">
        {hasSituacao ? (
          <div className="rv-view-card">
            <button
              className="rv-view-situacao-btn"
              type="button"
              onClick={() => setSituacaoOpen((previous) => !previous)}
            >
              Situação Geral
              {situacaoOpen ? (
                <ChevronUp className="rv-view-situacao-icon" size={18} />
              ) : (
                <ChevronDown className="rv-view-situacao-icon" size={18} />
              )}
            </button>
            {situacaoOpen ? (
              <p className="rv-view-situacao-text">{situacaoGeral}</p>
            ) : null}
          </div>
        ) : null}

        {grupoTabs.length > 0 ? (
          <div className="rv-view-grupos">
            <div className="rv-view-grupos-head">
              <h2 className="rv-view-section-title">Galeria de Evidências</h2>
              <span className="rv-view-grupos-count">
                {totalPontos} registros monitorados
              </span>
            </div>
            {grupoTabs.map((tab) => (
              <div
                key={tab.key}
                data-section-key={tab.key}
                ref={(node) => {
                  sectionRefs.current[tab.key] = node;
                }}
                className="rv-view-section"
              >
                <GrupoSection
                  gleba={tab.gleba}
                  grupo={tab.grupo}
                  resumoOcorrencias={tab.resumoOcorrencias}
                />
              </div>
            ))}
          </div>
        ) : null}

        {especialTabs.length > 0 ? (
          <div className="rv-view-especial-grid">
            {especialTabs.map((tab) => (
              <div
                key={tab.key}
                data-section-key={tab.key}
                ref={(node) => {
                  sectionRefs.current[tab.key] = node;
                }}
                className="rv-view-section"
              >
                {tab.kind === 'recomendacoes' ? (
                  <RecomendacoesSection
                    recomendacoes={relatorio.recomendacoes}
                  />
                ) : (
                  <ChuvaSection chuva={relatorio.chuvaAcumulada} />
                )}
              </div>
            ))}
          </div>
        ) : null}
      </div>

      <footer className="rv-view-footer">
        <div className="rv-view-footer-inner">
          {header?.proximaVisita && header.proximaVisita !== '-' ? (
            <AqilaFooterBar
              icon={<CalendarClock />}
              label="Próxima Visita:"
              value={formatFullDate(header.proximaVisita)}
            />
          ) : null}

          <div className="rv-view-footer-credit">
            <div className="rv-view-footer-dev">
              Desenvolvido por:
              <img
                alt="Agro1"
                className="rv-view-footer-logo"
                src={agro1Logo}
              />
            </div>
            <p className="rv-view-footer-copy">
              {`© ${new Date().getFullYear()} Relatório de Visita Técnica AQILA - Todos os direitos reservados`}
            </p>
          </div>
        </div>
      </footer>

      {monitorModalOpen ? (
        <div
          role="presentation"
          className="rv-view-monitor-overlay"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setMonitorModalOpen(false);
            }
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') setMonitorModalOpen(false);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            className="rv-view-monitor-dialog"
          >
            <div className="rv-view-monitor-header">
              <span className="rv-view-monitor-title">Monitores da Visita</span>
              <button
                type="button"
                aria-label="Fechar"
                className="rv-view-monitor-close"
                onClick={() => setMonitorModalOpen(false)}
              >
                <X size={16} />
              </button>
            </div>
            <div className="rv-view-monitor-list">
              {monitores.map((monitor, index) => (
                <div
                  key={monitor.id ?? monitor.nome}
                  className="rv-view-monitor-item"
                >
                  <span
                    className="rv-view-monitor-avatar"
                    style={{
                      background: AVATAR_COLORS[index % AVATAR_COLORS.length],
                    }}
                  >
                    {getInitials(orDash(monitor.nome))}
                  </span>
                  <div className="rv-view-monitor-name-col">
                    <span className="rv-view-monitor-name">
                      {orDash(monitor.nome)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default RelatorioVisitaViewPage;
